import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Inbox, FileCheck, ClipboardList, FileSignature, FileBox, ArrowRight, CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';

const TIPOS = [
  { key: 'oc_vb',       label: 'OC sin VB',         icon: FileCheck,      color: 'text-blue-700' },
  { key: 'requisicion', label: 'Requisiciones',     icon: ClipboardList,  color: 'text-purple-700' },
  { key: 'parte_vb',    label: 'Partes Diarios',    icon: FileSignature,  color: 'text-emerald-700' },
  { key: 'proforma',    label: 'Proformas',         icon: FileBox,        color: 'text-amber-700' },
];

/**
 * Widget de Bandeja de Aprobaciones — primera tarjeta del DashboardGerencia.
 * Lee /api/aprobaciones/count y muestra contador prominente con breakdown
 * por tipo + CTA directa a /aprobaciones. Refresca cada 60s.
 */
export function BandejaAprobacionesWidget() {
  const [counts, setCounts] = useState({ total: 0, oc_vb: 0, requisicion: 0, parte_vb: 0, proforma: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/aprobaciones/count');
      setCounts(res.data?.counts || {});
    } catch {
      // si falla, mantenemos lo último visto (no rompemos el dashboard)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 60_000);
    return () => clearInterval(i);
  }, [load]);

  const total = counts.total || 0;

  // Skeleton mientras carga la primera respuesta (evita flash con count 0).
  if (loading) {
    return (
      <Card className="border-l-4 border-l-gray-300" data-testid="bandeja-aprobaciones-loading">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-md bg-gray-200 animate-pulse">
              <Inbox className="w-7 h-7 text-gray-400" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="h-7 w-24 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Variante "todo limpio" — card chica, verde
  if (total === 0) {
    return (
      <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/40" data-testid="bandeja-aprobaciones-empty">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-900">Bandeja de Aprobaciones</p>
              <p className="text-xs text-emerald-700">Sin pendientes — todo aprobado.</p>
            </div>
          </div>
          <Link to="/aprobaciones">
            <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-100" data-testid="btn-ir-aprobaciones-empty">
              Ver historial <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  // Variante "con pendientes" — card grande/prominente, ámbar
  return (
    <Card
      className="border-l-4 border-l-amber-500 bg-gradient-to-br from-amber-50/70 to-white shadow-md hover:shadow-lg transition-shadow"
      data-testid="bandeja-aprobaciones-widget"
    >
      <CardContent className="p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-md bg-amber-500/15 text-amber-700 shrink-0">
              <Inbox className="w-7 h-7" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-amber-800">
                Bandeja de Aprobaciones
              </p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-4xl font-bold text-amber-900 tabular-nums" data-testid="aprobaciones-total">
                  {total}
                </span>
                <span className="text-sm text-amber-700">
                  pendiente{total === 1 ? '' : 's'} de tu firma
                </span>
              </div>

              {/* Breakdown por tipo */}
              <div className="flex flex-wrap gap-3 mt-2.5">
                {TIPOS.map(({ key, label, icon: Icon, color }) => {
                  const n = counts[key] || 0;
                  if (n === 0) return null;
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-white/70 border border-amber-200"
                      data-testid={`chip-aprob-${key}`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${color}`} />
                      <span className="text-gray-700">{label}:</span>
                      <span className="font-semibold tabular-nums text-gray-900">{n}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="md:shrink-0">
            <Link to="/aprobaciones">
              <Button
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                data-testid="btn-ir-aprobaciones"
              >
                Revisar y aprobar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default BandejaAprobacionesWidget;
